import { Auth } from '@aws-amplify/auth'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { AxiosError } from 'axios'

import { getCustomerApiClient } from 'api/CustomerApiClient'
import { toast } from 'components/ui/toaster'
import AuthUtils from 'utils/AuthUtils'

import { i18n } from '../i18n/config'

const changePassword = async ({
  oldPassword,
  newPassword
}: {
  oldPassword: string
  newPassword: string
}): Promise<'SUCCESS' | void> => {
  try {
    const user: CognitoUser = await Auth.currentAuthenticatedUser()

    const authResp = await Auth.changePassword(user, oldPassword, newPassword)

    await getCustomerApiClient().createCustomEntityActivity(undefined, {
      type: 'PortalUserResetPassword'
    })

    return authResp
  } catch (error) {
    toast.error({
      title: i18n.t('common.error.something_went_wrong'),
      description: error.message
    })
  }
}

const changeEmail = async ({
  email,
  password
}: {
  email: string
  password: string
}): Promise<
  | 'SUCCESS'
  | 'EMAIL_ALREADY_EXISTS'
  | 'INCORRECT_PASSWORD'
  | 'REQUIRE_STRONG_PASSWORD'
  | 'EMAIL_IS_SAME'
  | string
> => {
  try {
    await getCustomerApiClient().updatePortalUserEmail(undefined, {
      email,
      password
    })

    return 'SUCCESS'
  } catch (error) {
    const errorMessage = error.response?.data?.error

    if (
      errorMessage === 'Email already exists, please provide a different email.'
    ) {
      return 'EMAIL_ALREADY_EXISTS'
    }

    if (errorMessage === 'Incorrect password') {
      return 'INCORRECT_PASSWORD'
    }

    if (
      errorMessage ===
      'We require a strong password. Please update your current password and try again.'
    ) {
      return 'REQUIRE_STRONG_PASSWORD'
    }

    if (errorMessage === 'Old and new email should not be same') {
      return 'EMAIL_IS_SAME'
    }

    return error.message
  }
}

const signOut = async () => {
  const showErrorIfUnexpected = (error: Error) => {
    const isExpectedError =
      error.message?.includes('revoked') ||
      (error as AxiosError)?.response?.status === 401

    if (!isExpectedError) {
      toast.error({
        title: i18n.t('common.error.something_went_wrong'),
        description: error.message
      })
    }
  }

  try {
    const token = await AuthUtils.getRefreshToken()

    if (token) {
      // Used to revoke token
      await getCustomerApiClient().revokeToken(null, { refresh_token: token })
    }

    // Used to clean up the local state and trigger events
    await AuthUtils.signOut()
  } catch (error) {
    console.warn('Error during sign out:', error)

    showErrorIfUnexpected(error)
  }
}

export { changePassword, changeEmail, signOut }
